import { Component, Input, OnChanges } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { casasDecimais, compacto, formatarValor, numerico, rotuloData } from './formato-relatorio';

interface ColunaVisivel {
  coluna: string;
  rotulo: string;
  tipo: string;
  formato: string;
  total: string;
  direita: boolean;
  casas: number;
  abaixo: boolean;
}

interface GraficoPronto {
  id: string;
  titulo: string;
  tipo: string;
  tipoChart: string;
  dados: any;
  opcoes: any;
  vazio: boolean;
  nota: string;
}

interface Bloco {
  bloco: string;
  largura: string;
  grafico?: GraficoPronto;
}

const LARANJA = '#ff7800';
const CINZA = '#b9c0c7';
const PALETA = ['#ff7800', '#2f3439', '#1d6fb8', '#17613a', '#a34a00', '#7a5cc2', '#c2185b', '#00897b', '#8d6e63', '#546e7a'];

@Component({
  selector: 'habil-resultado-relatorio',
  templateUrl: './resultado-relatorio.component.html',
  styleUrls: ['./resultado-relatorio.component.scss'],
})
export class ResultadoRelatorioComponent implements OnChanges {
  @Input() resultado: any;

  colunas: ColunaVisivel[] = [];
  colunasAbaixo: ColunaVisivel[] = [];
  colunaArvore: ColunaVisivel | null = null;
  colunasTabela: ColunaVisivel[] = [];
  private cacheAbaixo = new WeakMap<any, { rotulo: string; texto: string }[]>();
  private readonly semAbaixo: { rotulo: string; texto: string }[] = [];
  nos: TreeNode[] = [];
  agrupado = false;
  tituloGrupo = '';
  totalGeral: any = null;
  variaveisTotal: { rotulo: string; texto: string }[] = [];
  private metaVariaveis: any[] = [];
  blocos: Bloco[] = [];
  linhasPorPagina = 50;
  primeira = 0;

  ngOnChanges(): void {
    this.primeira = 0;
    this.montar();
  }

  trocarPagina(evento: { first: number; rows: number }): void {
    this.primeira = evento.first;
    this.linhasPorPagina = evento.rows;
  }

  formatar(valor: any, coluna: ColunaVisivel): string {
    return formatarValor(valor, coluna.tipo, coluna.formato, coluna.casas);
  }

  formatarTotal(valor: any, coluna: ColunaVisivel): string {
    return coluna.total === 'contagem' || coluna.total === 'distintos' ? formatarValor(valor, 'inteiro') : this.formatar(valor, coluna);
  }

  linhasAbaixo(linha: any): { rotulo: string; texto: string }[] {
    if (!linha || !this.colunasAbaixo.length) {
      return this.semAbaixo;
    }
    let lista = this.cacheAbaixo.get(linha);
    if (!lista) {
      lista = [];
      for (const c of this.colunasAbaixo) {
        const texto = this.formatar(linha[c.coluna], c).trim();
        if (texto) {
          lista.push({ rotulo: c.rotulo, texto });
        }
      }
      this.cacheAbaixo.set(linha, lista);
    }
    return lista;
  }

  formatarIndicador(indicador: any): string {
    return formatarValor(indicador.valor, indicador.formato);
  }

  private montar(): void {
    const tabela = this.resultado?.tabela;
    if (!tabela) {
      this.colunas = [];
      this.colunasAbaixo = [];
      this.colunaArvore = null;
      this.colunasTabela = [];
      this.nos = [];
      this.blocos = [];
      return;
    }
    const agrupamentos: any[] = tabela.agrupamentos || [];
    const agrupadas = new Set(agrupamentos.map((a) => a.coluna));
    const linhas = this.todasAsLinhas(tabela);
    const todas: ColunaVisivel[] = (tabela.colunas || [])
      .filter((c: any) => !agrupadas.has(c.coluna))
      .map((c: any) => ({
        coluna: c.coluna,
        rotulo: c.rotulo || c.coluna,
        tipo: c.tipo,
        formato: c.formato,
        total: c.total,
        direita: numerico(c.formato || c.tipo),
        casas: (c.formato || c.tipo) === 'decimal' ? casasDecimais(linhas.map((l) => l[c.coluna])) : 2,
        abaixo: !!c.abaixo,
      }));
    this.colunas = todas.filter((c) => !c.abaixo);
    this.colunasAbaixo = todas.filter((c) => c.abaixo);
    this.cacheAbaixo = new WeakMap();
    this.agrupado = agrupamentos.length > 0;
    const primeira = this.colunas[0];
    this.colunaArvore = this.agrupado && primeira && !primeira.direita && !primeira.total && this.colunas.length > 1 ? primeira : null;
    this.colunasTabela = this.colunaArvore ? this.colunas.slice(1) : this.colunas;
    this.tituloGrupo = [...agrupamentos.map((a) => a.rotulo || a.coluna), ...(this.colunaArvore ? [this.colunaArvore.rotulo] : [])].join(' › ');
    this.totalGeral = tabela.totalGeral || null;
    this.metaVariaveis = tabela.variaveis || [];
    this.variaveisTotal = this.valoresVariaveis(tabela.variaveisTotal);
    if (this.agrupado) {
      this.nos = (tabela.grupos || []).map((g: any) => this.noGrupo(g, agrupamentos));
    } else {
      this.nos = linhas.map((l: any) => ({ data: { linha: l } }));
    }
    this.montarBlocos();
  }

  private montarBlocos(): void {
    const graficos = new Map<string, GraficoPronto>();
    for (const g of this.resultado.graficos || []) {
      graficos.set(g.id, this.grafico(g));
    }
    const layout: any[] = this.resultado.layout?.length
      ? this.resultado.layout
      : [
          ...(this.resultado.indicadores?.length ? [{ bloco: 'indicadores', largura: 'inteira' }] : []),
          ...[...graficos.keys()].map((id) => ({ bloco: 'grafico', grafico: id, largura: 'metade' })),
          { bloco: 'tabela', largura: 'inteira' },
        ];
    this.blocos = layout
      .filter((b) => b.bloco !== 'grafico' || graficos.has(b.grafico))
      .filter((b) => b.bloco !== 'indicadores' || this.resultado.indicadores?.length)
      .map((b) => ({ bloco: b.bloco, largura: b.largura || 'inteira', grafico: b.bloco === 'grafico' ? graficos.get(b.grafico) : undefined }));
  }

  private grafico(g: any): GraficoPronto {
    const pontos: any[] = g.pontos || [];
    const rotulos = pontos.map((p) =>
      g.agruparData ? rotuloData(p.categoria, g.agruparData) : p.categoria === null || p.categoria === undefined || p.categoria === '' ? '(vazio)' : formatarValor(p.categoria, g.categoriaTipo)
    );
    const valores = pontos.map((p) => (p.valor === null || p.valor === undefined ? null : Number(p.valor)));
    const temOutros = g.outros !== undefined && g.outros !== null;
    if (temOutros) {
      rotulos.push(`Outros (${g.qtdeOutros})`);
      valores.push(Number(g.outros));
    }
    const pizza = g.tipo === 'pizza' || g.tipo === 'rosca';
    const linha = g.tipo === 'linha' || g.tipo === 'area';
    const cores = pizza
      ? valores.map((_, i) => (temOutros && i === valores.length - 1 ? CINZA : PALETA[i % PALETA.length]))
      : valores.map((_, i) => (temOutros && i === valores.length - 1 ? CINZA : LARANJA));
    const formato = g.formato;
    const conjunto: any = {
      label: g.titulo || '',
      data: valores,
      backgroundColor: linha ? 'rgba(255, 120, 0, 0.14)' : cores,
      borderColor: linha ? '#e86c00' : pizza ? '#ffffff' : cores,
      borderWidth: linha ? 2 : pizza ? 2 : 0,
      borderRadius: pizza || linha ? 0 : 4,
      fill: g.tipo === 'area',
      tension: 0.25,
      pointRadius: linha && valores.length <= 31 ? 3 : 0,
      pointBackgroundColor: '#e86c00',
      maxBarThickness: 36,
    };
    const dica = {
      callbacks: {
        label: (ctx: any) => ` ${formatarValor(ctx.raw, formato, formato, formato === 'decimal' ? 2 : undefined)}`,
      },
    };
    const eixoValor = {
      beginAtZero: true,
      grid: { color: '#eef0f2', drawBorder: false },
      ticks: { color: '#5f676f', font: { size: 11 }, callback: (v: any) => compacto(Number(v), formato) },
    };
    const eixoCategoria = {
      grid: { display: false, drawBorder: false },
      ticks: { color: '#5f676f', font: { size: 11 }, autoSkip: true, maxRotation: 0 },
    };
    const horizontal = g.tipo === 'barrasHorizontais';
    const opcoes: any = {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 250 },
      plugins: {
        legend: pizza ? { display: true, position: 'right', labels: { boxWidth: 12, font: { size: 11 }, color: '#495057' } } : { display: false },
        tooltip: dica,
      },
    };
    if (!pizza) {
      opcoes.indexAxis = horizontal ? 'y' : 'x';
      opcoes.scales = horizontal ? { x: eixoValor, y: eixoCategoria } : { x: eixoCategoria, y: eixoValor };
    } else if (g.tipo === 'rosca') {
      opcoes.cutout = '58%';
    }
    return {
      id: g.id,
      titulo: g.titulo || g.categoriaRotulo,
      tipo: g.tipo,
      tipoChart: pizza ? (g.tipo === 'rosca' ? 'doughnut' : 'pie') : linha ? 'line' : 'bar',
      dados: { labels: rotulos, datasets: [conjunto] },
      opcoes,
      vazio: !pontos.length,
      nota: temOutros ? `Os ${pontos.length} maiores. O resto soma em "Outros".` : '',
    };
  }

  private todasAsLinhas(tabela: any): any[] {
    if (!tabela.agrupamentos?.length) {
      return tabela.linhas || [];
    }
    const linhas: any[] = [];
    const visitar = (g: any) => {
      if (g.grupos) {
        g.grupos.forEach(visitar);
      } else {
        linhas.push(...(g.linhas || []));
      }
    };
    (tabela.grupos || []).forEach(visitar);
    return linhas;
  }

  private noGrupo(grupo: any, agrupamentos: any[]): TreeNode {
    const def = agrupamentos[grupo.nivel] || {};
    const filhos: TreeNode[] = grupo.grupos
      ? grupo.grupos.map((g: any) => this.noGrupo(g, agrupamentos))
      : (grupo.linhas || []).map((l: any) => ({ data: { linha: l, nivel: grupo.nivel + 1 } }));
    return {
      data: {
        grupo: true,
        nivel: grupo.nivel,
        rotulo: this.rotuloGrupo(grupo.valor, def),
        qtde: grupo.qtde,
        totais: grupo.totais || {},
        variaveis: this.valoresVariaveis(grupo.variaveis),
      },
      children: filhos,
      expanded: !def.recolhido,
      leaf: filhos.length === 0,
    };
  }

  private valoresVariaveis(valores: any): { rotulo: string; texto: string }[] {
    if (!valores || !this.metaVariaveis.length) {
      return [];
    }
    return this.metaVariaveis
      .filter((v) => v.nome in valores)
      .map((v) => ({ rotulo: v.rotulo || v.nome, texto: formatarValor(valores[v.nome], v.formato, v.formato, v.formato === 'decimal' ? 2 : undefined) || '—' }));
  }

  private rotuloGrupo(valor: any, def: any): string {
    if (valor === null || valor === undefined || valor === '') {
      return '(vazio)';
    }
    return formatarValor(valor, def.tipo);
  }
}
